import { ReactNode, useEffect, useMemo, useReducer, useState } from 'react';

import {
  Counter,
  ChakraBox,
  ChakraFlex,
  ChakraTab,
  ChakraTabList,
  ChakraTabs,
  Text,
} from '@equip.health/ui';
import { ErrorBoundary } from 'react-error-boundary';
import { useHistory } from 'react-router-dom';
import { PatientHeaderErrorFallback } from '~/components/maud/patients/profile/patient-header/PatientHeaderErrorFallback';
import { ApiCommand } from '~/lib/Api';
import { PAGE_LAYOUT } from '~/lib/constants';
import { Listeners } from '~/lib/constants/global.constants';
import {
  AssessmentAction,
  AssessmentReducer,
  AssessmentState,
  CreateAssessmentIntent,
  iframeContainerTabs,
  PatientTabs,
  PatientTabsDefinition,
  UpdateAssessmentIntent,
} from '~/lib/constants/patients.constants';
import { usePatientTabsCollection } from '~/lib/hooks/usePatientTabsCollection';
import { SurveyName } from '~/lib/constants/survey/SurveyName';
import { SurveyResponseSource } from '~/lib/constants/survey/SurveyResponseSource';
import { SurveyResponseStatus } from '~/lib/constants/survey/SurveyResponseStatus';
import urlConstants from '~/lib/constants/url.constants';
import {
  PortalComponent,
  useComponentPortalContext,
} from '~/lib/contexts/ComponentPortalContext';
import { useGlobalContext } from '~/lib/contexts/GlobalContext';
import { useTaskContext } from '~/lib/contexts/TaskContext';
import { useApi } from '~/lib/hooks';

import {
  getPreferredFirstName,
  getPreferredFullName,
} from '~/lib/util/patient.util';
import { ClinicianAssessmentsSurveyCards } from '~/lib/util/survey.util';
import { onContinueSurvey } from '~/lib/util/surveyActions.util';
import {
  CompServTask,
  FetchTasksResponse,
  getCombinedSortedTasksForPatient,
} from '~/lib/util/task.util';
import util from '~/lib/util/util';
import { Path } from '~/routes/constants';
import { useAppDispatch } from '~/store/hooks';
import {
  useCreateAssessmentMutation,
  useGetAssessmentsListQuery,
  useGetSurveysQuery,
  useUpdateAssessmentMutation,
} from '~/store/services/assessments';
import { useGetPatientClinicalProfileQuery } from '~/store/services/clinicalData';
import { useGetPatientDiagnosesQuery } from '~/store/services/diagnoses';

import PatientHeader from './patient-header';
import PatientTabPanels from './tabs';
import {
  patientsApi,
  useGetPatientContactsQuery,
} from '~/store/services/patients';
import AnalyticsEvents from '~/analytics';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';
import { PatientCalendarEvents } from '~/analytics/patientsCalendarEvents';
import { CarePlanEvents } from '~/analytics/carePlanEvents';

const assessmentReducer = (
  state: AssessmentState,
  action: AssessmentAction,
): AssessmentState => {
  const { field, data } = action;
  if (field === 'SET') return { ...state, ...data };
  return { ...state, [field]: data };
};

interface PatientProfileProps {
  detailsTabId?: string;
  familyTasksData?: FetchTasksResponse;
  fetchTasksData: () => void;
  isTasksError?: boolean;
  maudTasksData?: FetchTasksResponse;
  patient: Patient;
  selectedTaskTab?: number;
  setSelectedTaskTab?: (selectedTaskTab: number) => void;
  tab: string;
}

const PatientProfile = ({
  detailsTabId,
  familyTasksData,
  fetchTasksData,
  isTasksError,
  maudTasksData,
  patient,
  selectedTaskTab = 0,
  setSelectedTaskTab,
  tab,
}: PatientProfileProps) => {
  const {
    chosenName,
    email,
    firstName,
    lastName,
    phoneNumber,
    userId: patientId,
    isAccountActive: patientIsAccountActive,
    hasAuthId: patientHasAuthId,
  } = patient ?? {};
  const appDispatch = useAppDispatch();
  const { trackEvent } = useAnalytics();
  const { data: patientClinicalProfile } = useGetPatientClinicalProfileQuery(
    patientId,
    {
      skip: !patientId,
    },
  );

  const shouldFetchContacts =
    !!patientId && !(patientIsAccountActive && patientHasAuthId);

  const { data: patientContactList } = useGetPatientContactsQuery(patientId, {
    skip: !shouldFetchContacts,
  });

  const phoneNumberFromPatientProfile =
    util.parsePhoneNumberWithDefaultCountry(phoneNumber);
  const patientPreferredFullName = getPreferredFullName(
    chosenName,
    firstName,
    lastName,
  );
  const [tabIndex, setTabIndex] = useState(0);

  const [patientTasksCount, setPatientTasksCount] = useState(0);

  const [assessmentState, updateAssessmentState] =
    useReducer<AssessmentReducer>(assessmentReducer, {
      assessmentList: [],
      createAssessmentIntent: null,
      emailSentData: null,
      isAssessmentListError: false,
      isAssessmentListLoading: false,
      selectedAssessment: null,
      selectedSurveyResults: null,
      updateAssessmentIntent: null,
    });
  const {
    createAssessmentIntent,
    emailSentData,
    selectedAssessment,
    updateAssessmentIntent,
  } = assessmentState;
  const { toUserEmail, toUserFirstName, toUserLastName, toUserPhone } =
    selectedAssessment ?? {};

  const history = useHistory();

  const {
    setIsOnPatientProfile,
    addListener,
    currentUser,
    removeListener,
    setCurrentPatientGender,
  } = useGlobalContext();
  const {
    firstName: currentUserFirstName,
    lastName: currentUserLastName,
    userId: currentUserId,
  } = currentUser;

  const { setComponentVisibility, addEditSurveyRespondentAccordion } =
    useComponentPortalContext();
  const { data, hasUnsavedChanges, isExpanded } =
    addEditSurveyRespondentAccordion ?? {};

  const { categories } = useTaskContext();

  const { data: getSurveysResponse } = useGetSurveysQuery({
    filterBy: { surveyName: SurveyName.ADMISSIONS_INTAKE },
  });

  const {
    data: assessmentsList,
    error: assessmentsListError,
    isLoading: isAssessmentsListLoading,
    refetch: refetchAssessmentsList,
  } = useGetAssessmentsListQuery(
    { patientId },
    {
      skip: !patientId,
    },
  );

  const [
    createAssessment,
    {
      data: createAssessmentData,
      isLoading: isCreateAssessmentLoading,
      error: createAssessmentError,
    },
  ] = useCreateAssessmentMutation();

  const {
    data: updateAssessmentRespondentData,
    error: updateAssessmentRespondentError,
    loading: isUpdateAssessmentRespondentProcessing,
    resetError: resetUpdateAssessmentRespondentError,
    sendRequest: updateAssessmentRespondent,
  } = useApi<string>();

  const [
    updateAssessment,
    {
      data: updateAssessmentData,
      isLoading: isUpdateAssessmentLoading,
      error: updateAssessmentError,
    },
  ] = useUpdateAssessmentMutation();

  const { data: patientDiagnoses } = useGetPatientDiagnosesQuery(patientId, {
    skip: !patientId,
  });
  const didSendEmail =
    updateAssessmentData &&
    !updateAssessmentError &&
    updateAssessmentIntent === UpdateAssessmentIntent.SEND_INVITATION;

  const {
    error: sendEmailError,
    loading: isSendEmailProcessing,
    sendRequest: sendEmail,
    statusCode: sendEmailStatusCode,
  } = useApi();

  const patientTabAddons = useMemo<
    Partial<Record<PatientTabs, ReactNode>>
  >(() => {
    let addon: ReactNode = null;

    if (patientTasksCount > 0) {
      addon = (
        <ChakraBox marginLeft="6px">
          <Counter
            id="patient-menu__tasks-counter"
            number={patientTasksCount}
          />
        </ChakraBox>
      );
    }

    return {
      [PatientTabs.TASKS]: addon,
    };
  }, [patientTasksCount]);
  const isAssessmentRequestProcessing =
    isCreateAssessmentLoading ||
    isSendEmailProcessing ||
    isUpdateAssessmentLoading ||
    isUpdateAssessmentRespondentProcessing;

  const onCreateAssessment = ({
    assigneeExternalId,
    status,
    surveyName,
    toUserEmail,
    toUserFirstName,
    toUserLastName,
    toUserPhone,
  }: Partial<SurveyResponse>): void => {
    createAssessment({
      assigneeExternalId,
      status: status as SurveyResponseStatus,
      submittedSource: SurveyResponseSource.WEB,
      submittedUserFirstName: currentUserFirstName,
      submittedUserId: currentUserId,
      submittedUserLastName: currentUserLastName,
      surveyName: surveyName as SurveyName,
      toUserEmail,
      toUserFirstName,
      toUserLastName,
      toUserPhone,
      userId: patientId,
    });
  };

  const onUpdateAssessmentRespondentDetails = ({
    responseExternalId,
    toUserEmail,
    toUserFirstName,
    toUserLastName,
    toUserPhone,
  }: Partial<SurveyResponse>) => {
    updateAssessmentRespondent({
      command: ApiCommand.PATCH,
      options: {
        toUserEmail,
        toUserFirstName,
        toUserLastName,
        toUserPhone,
      },
      url: urlConstants.assessments.updateAssessmentRespondent(
        responseExternalId,
      ),
    });
  };

  const UpdateRespondentDialogCallbackMapper: Record<
    UpdateAssessmentIntent,
    (updatedAssessment: Partial<SurveyResponse>) => void
  > = {
    [UpdateAssessmentIntent.SEND_INVITATION]: onCreateAssessment,
    [UpdateAssessmentIntent.UPDATE_RESPONDENT]:
      onUpdateAssessmentRespondentDetails,
  };

  const handleRespondentConfirmClick = ({
    assigneeExternalId,
    toUserEmail,
    toUserFirstName,
    toUserLastName,
    toUserPhone,
  }: SurveyRespondentProps): void => {
    const updated = {
      assigneeExternalId,
      ...assessmentState.selectedAssessment,
      toUserEmail,
      toUserFirstName,
      toUserLastName,
      toUserPhone,
    };
    if (assessmentState.createAssessmentIntent !== null) {
      onCreateAssessment(updated);
    }
    if (assessmentState.updateAssessmentIntent !== null) {
      UpdateRespondentDialogCallbackMapper[updateAssessmentIntent]?.(updated);
    }
    updateAssessmentState({ data: updated, field: 'selectedAssessment' });
  };

  const handleRespondentAccordionClose = (
    ...fields: (keyof AssessmentState)[]
  ): void => {
    resetUpdateAssessmentRespondentError();
    const data = { selectedAssessment: null };
    // Reset state
    fields?.forEach((field: keyof AssessmentState) => {
      data[field] = null;
    });
    updateAssessmentState({ data, field: 'SET' });
  };

  const handleShowEmailSentToast = (): void => {
    const { status, surveyName, toUserEmail } = emailSentData ?? {};

    const statusPart =
      status === SurveyResponseStatus.NEW ? 'invitation' : 'reminder';

    const toEmailPart = toUserEmail ? ` to ${toUserEmail}` : '';

    let message = `${surveyName} survey ${statusPart} email sent${toEmailPart}`;
    let title = `${surveyName} survey email sent`;

    // Handle scenario where email is generated automatically on backend when status changes (sendEmailStatusCode is null)
    // and when provider manually triggers email send
    if (sendEmailStatusCode && sendEmailStatusCode !== 200) {
      message = `Unable to send ${surveyName} survey ${statusPart} email${toEmailPart}`;
      title = `${surveyName} survey email failure`;
    }

    setComponentVisibility({
      component: PortalComponent.TOAST_ACCORDION,
      data: { message, title },
      isExpanded: true,
      isVisible: true,
    });
  };

  const onSendEmail = ({
    responseExternalId,
    status,
    surveyName,
    toUserEmail,
  }: Partial<SurveyResponse>): void => {
    sendEmail({
      command: ApiCommand.POST,
      options: {
        requestedByUserFirstName: currentUserFirstName,
        requestedByUserLastName: currentUserLastName,
        responseExternalId,
      },
      url: urlConstants.assessments.sendEmail,
    });
    updateAssessmentState({
      data: { status, surveyName, toUserEmail },
      field: 'emailSentData',
    });
  };

  const onUpdateAssessmentStatus = (
    status: SurveyResponseStatus,
    toUserEmail: string,
    responseExternalId: string,
  ) =>
    updateAssessment({
      responseExternalId,
      assessment: {
        status,
        submittedSource: SurveyResponseSource.WEB,
        submittedUserFirstName: currentUserFirstName,
        submittedUserId: currentUserId,
        submittedUserLastName: currentUserLastName,
        toUserEmail,
        userTimezoneId: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
    });

  const fetchPatientDetails = () => {
    appDispatch(
      patientsApi.endpoints.getPatient.initiate(
        { patientId },
        {
          forceRefetch: true,
        },
      ),
    );
  };

  useEffect(() => {
    fetchPatientDetails();

    addListener(
      Listeners.PATIENTPROFILE_PATIENTBASICINFO_UPDATE,
      fetchPatientDetails,
    );
    addListener(Listeners.PATIENTMENU_TASKDETAILS_CREATE, fetchTasksData);
    addListener(Listeners.PATIENTMENU_TASKDETAILS_UPDATE, fetchTasksData);

    return () => {
      setIsOnPatientProfile(false);
      removeListener(Listeners.PATIENTPROFILE_PATIENTBASICINFO_UPDATE);
      removeListener(Listeners.PATIENTMENU_TASKDETAILS_CREATE);
      removeListener(Listeners.PATIENTMENU_TASKDETAILS_UPDATE);
    };
  }, [patientId]);

  useEffect(() => {
    setCurrentPatientGender(patient?.genderOnRecord);
  }, [patient]);

  useEffect(() => {
    if (patient?.userId) {
      setIsOnPatientProfile(true);
    }
  }, [patient?.userId]);

  /*
    After an assessment is created, close the Survey Respondent accordion and open the survey accordion
    if the provider intends to begin the assessment immediately.

    Else if the provider intends to send an email invitation, send a new request to update the
    assessment status to "requested".
  */
  useEffect(() => {
    const { responseExternalId } = createAssessmentData ?? {};
    if (responseExternalId) {
      if (createAssessmentIntent === CreateAssessmentIntent.BEGIN_NOW) {
        const match = assessmentsList?.assessments?.find(
          ({ responseExternalId: extId }: SurveyResponse) =>
            extId === responseExternalId,
        );
        if (match) {
          setComponentVisibility({
            component: PortalComponent.SURVEY_ACCORDION,
            data: match,
            isExpanded: true,
            isVisible: true,
          });
          handleRespondentAccordionClose('createAssessmentIntent');
        }
      } else if (
        createAssessmentIntent === CreateAssessmentIntent.SEND_INVITATION
      ) {
        const data = {
          ...assessmentState.selectedAssessment,
          ...createAssessmentData,
        };
        onUpdateAssessmentStatus(
          SurveyResponseStatus.REQUESTED,
          data.toUserEmail,
          data.responseExternalId,
        );
        updateAssessmentState({
          data: {
            emailSentData: data,
            updateAssessmentIntent: UpdateAssessmentIntent.SEND_INVITATION,
          },
          field: 'SET',
        });
      }
    }
  }, [createAssessmentData?.responseExternalId, assessmentsList]);

  /* After survey respondent details are updated, close the accordion */
  useEffect(() => {
    if (updateAssessmentRespondentData) {
      refetchAssessmentsList(); // TODO when updateAssessmentRespondent is converted to RTK query, remove this call
      handleRespondentAccordionClose('updateAssessmentIntent');
    }
  }, [updateAssessmentRespondentData]);

  /*
    After an assessment is updated, if the provider intends to send an email invitation,
    close the survey respondent accordion and show a success toast.
  */
  useEffect(() => {
    if (didSendEmail) {
      handleRespondentAccordionClose(
        'createAssessmentIntent',
        'updateAssessmentIntent',
      );
      handleShowEmailSentToast();
    }
  }, [didSendEmail]);

  const phoneNumberFromRespondentAccordion =
    util.parsePhoneNumberWithDefaultCountry(data?.toUserPhone);

  const hasEquipAccount = useMemo(() => {
    const patientHasAccount = patient?.isAccountActive && patient?.hasAuthId;

    if (patientHasAccount) return true;
    const contacts = patientContactList ?? [];

    return contacts.some(
      (contact) =>
        contact.isProxy === true &&
        contact.hasLoggedIn === true &&
        contact.hasSetPassword === true,
    );
  }, [patient, patientContactList]);

  const baseSurveyRespondentVisibilityProps = {
    component: PortalComponent.ADD_EDIT_SURVEY_RESPONDENT_ACCORDION,
    data: {
      ...data,
      hasEquipAccount,
      isCreateAssessmentError: !!createAssessmentError,
      isLoading: isAssessmentRequestProcessing,
      isSendEmailError: !!sendEmailError,
      isUpdateRespondentError: !!updateAssessmentRespondentError,
      onCancel: () =>
        handleRespondentAccordionClose(
          'createAssessmentIntent',
          'updateAssessmentIntent',
        ),
      onConfirm: handleRespondentConfirmClick,
      toUserEmail: toUserEmail ?? (data?.toUserEmail || email) ?? '',
      toUserFirstName:
        toUserFirstName ??
        (data?.toUserFirstName || getPreferredFirstName(chosenName, firstName)),
      toUserLastName:
        toUserLastName ?? (data?.toUserLastName || lastName) ?? '',
      toUserPhone:
        toUserPhone ??
        (phoneNumberFromRespondentAccordion || phoneNumberFromPatientProfile) ??
        '',
    },
    isExpanded,
    isVisible: !!selectedAssessment,
  };

  // Close any open billing-period accordion on patient profile change
  useEffect(() => {
    setComponentVisibility({
      component: PortalComponent.BILLING_PERIOD_ACCORDION,
      data: null,
      isExpanded: false,
      isVisible: false,
    });
  }, [patient?.userId]);

  // Update survey respondent accordion state if respondent name, email, or phone change OR if error state changes
  useEffect(() => {
    setComponentVisibility(baseSurveyRespondentVisibilityProps);
  }, [
    chosenName,
    createAssessmentError,
    email,
    firstName,
    hasEquipAccount,
    lastName,
    phoneNumber,
    sendEmailError,
    toUserEmail,
    toUserFirstName,
    toUserLastName,
    toUserPhone,
    updateAssessmentRespondentError,
  ]);

  // Update survey respondent accordion state if assessment is selected or unselected
  useEffect(() => {
    if (
      ClinicianAssessmentsSurveyCards.includes(selectedAssessment?.surveyName)
    ) {
      onCreateAssessment(selectedAssessment);
    } else {
      setComponentVisibility({
        ...baseSurveyRespondentVisibilityProps,
        data: selectedAssessment
          ? baseSurveyRespondentVisibilityProps.data
          : {},
        hasUnsavedChanges: !!selectedAssessment && hasUnsavedChanges,
        isExpanded: !!selectedAssessment,
        isVisible: !!selectedAssessment,
      });
    }
  }, [!!selectedAssessment]);

  // Update assessment state in response to survey respondent accordion closing
  useEffect(() => {
    if (!addEditSurveyRespondentAccordion?.isVisible) {
      updateAssessmentState({ data: null, field: 'selectedAssessment' });
    }
  }, [addEditSurveyRespondentAccordion?.isVisible]);

  // Show toast accordion after "send email" request
  useEffect(() => {
    if (sendEmailStatusCode) handleShowEmailSentToast();
  }, [sendEmailStatusCode]);

  useEffect(() => {
    const familyTaskCount = familyTasksData?.totalCount ?? 0;
    const maudTaskCount = maudTasksData?.totalCount ?? 0;
    setPatientTasksCount(familyTaskCount + maudTaskCount);
  }, [categories?.length, familyTasksData, maudTasksData]);

  const filteredPatientTabsCollections = usePatientTabsCollection(
    currentUser?.providerTypeDetails?.name,
  );

  const shouldDisableOuterScroll = iframeContainerTabs.has(tab);

  useEffect(() => {
    const filteredTabsIndexes = Object.values(
      filteredPatientTabsCollections,
    ).findIndex(({ subPath }) => subPath === tab);

    setTabIndex(filteredTabsIndexes);
  }, [tab]);

  const openTasks = useMemo<CompServTask[]>(() => {
    return getCombinedSortedTasksForPatient(maudTasksData, familyTasksData);
  }, [familyTasksData, maudTasksData]);

  return (
    <ChakraBox height="100vh">
      {patient && (
        <ChakraTabs
          defaultIndex={0}
          index={tabIndex}
          isLazy
          onChange={(index) => {
            setTabIndex(index);
          }}
          variant="line"
        >
          <ChakraFlex alignItems="stretch" direction="column" height="100vh">
            <ChakraBox flex="0 0 auto" paddingRight={PAGE_LAYOUT.rightSpacing}>
              <ErrorBoundary
                fallbackRender={(props) => (
                  <PatientHeaderErrorFallback {...props} />
                )}
              >
                <PatientHeader
                  onContinueSurvey={(externalId: string) => {
                    onContinueSurvey(
                      externalId,
                      assessmentsList?.assessments,
                      setComponentVisibility,
                    );
                  }}
                  patient={patient}
                  patientId={patientId}
                  patientPreferredFullName={patientPreferredFullName}
                  updateAssessmentState={updateAssessmentState}
                />
              </ErrorBoundary>
              <ChakraTabList
                borderBottom="1px solid"
                borderBottomColor="neutral.line"
                columnGap="10px"
                flexWrap="wrap"
                rowGap="2px"
              >
                {Object.entries(filteredPatientTabsCollections).map(
                  ([tab, { label, subPath }]: [
                    string,
                    PatientTabsDefinition,
                  ]) => {
                    const addon = patientTabAddons[tab];

                    return (
                      <ChakraTab
                        {...{
                          _hover: {
                            borderBottomColor: 'accent.blue',
                          },
                          _selected: {
                            backgroundColor: 'white',
                            borderColor: 'accent.blue',
                          },
                        }}
                        display="block"
                        key={subPath}
                        onClick={() => {
                          if (subPath === 'appointments') {
                            trackEvent(
                              PatientCalendarEvents.dailyViewTabButtonClicked,
                              {
                                patientId,
                                Source:
                                  AnalyticsSourceValues.PATIENT_CALENDAR_TAB,
                              },
                            );
                          }

                          if (subPath === 'messages') {
                            trackEvent(
                              AnalyticsEvents.patientChartMessagesTab,
                              {
                                patientId,
                                source:
                                  AnalyticsSourceValues.PATIENT_CHART_NAVIGATION,
                              },
                            );
                          }

                          if (subPath === 'care-planning') {
                            trackEvent(
                              CarePlanEvents.clickOnCarePlanActivityTab,
                              {
                                patientId,
                                source: AnalyticsSourceValues.CARE_PLAN_TAB,
                              },
                            );
                          }

                          if (subPath === 'journal') {
                            trackEvent(
                              AnalyticsEvents.journalTabButtonClicked,
                              {
                                patientId,
                                source: AnalyticsSourceValues.SMT_TAB,
                              },
                            );
                          }

                          history.push(
                            `${Path.MAUD_PATIENTS}/${patientId}/${subPath}`,
                          );
                        }}
                        padding="9px 8px 3px 8px"
                      >
                        <ChakraFlex
                          alignItems="center"
                          id="patient-profile__tab-label-container"
                          justifyContent="space-between"
                        >
                          <Text
                            color={
                              tabIndex === Number(tab)
                                ? 'neutral.primary'
                                : 'neutral.secondary'
                            }
                            id="patient-profile__tab-label"
                            variant={
                              tabIndex === Number(tab)
                                ? 'captionMedium'
                                : 'caption'
                            }
                          >
                            {label}
                          </Text>

                          {addon}
                        </ChakraFlex>
                      </ChakraTab>
                    );
                  },
                )}
              </ChakraTabList>
            </ChakraBox>
            <ChakraBox
              flex={1}
              overflow={shouldDisableOuterScroll ? 'hidden' : 'auto'}
            >
              <PatientTabPanels
                // TODO assessmentList prop can be removed, just use rtk-query to access the data whenever you want
                assessmentList={assessmentsList?.assessments ?? []}
                detailsTabId={detailsTabId}
                familyTaskCount={familyTasksData?.totalCount}
                isAssessmentError={
                  !!assessmentsListError ||
                  !!createAssessmentError ||
                  !!updateAssessmentError
                }
                isAssessmentLoading={
                  isAssessmentsListLoading || isAssessmentRequestProcessing
                }
                isTasksError={isTasksError}
                maudTaskCount={maudTasksData?.totalCount}
                onSendEmail={onSendEmail}
                onUpdateAssessmentStatus={onUpdateAssessmentStatus}
                openTasks={openTasks}
                patient={patient}
                patientClinicalProfile={patientClinicalProfile}
                patientDiagnoses={patientDiagnoses}
                patientEmail={patient.email}
                patientFirstName={getPreferredFirstName(
                  patient.chosenName,
                  patient.firstName,
                )}
                patientId={patientId}
                patientLastName={patient.lastName}
                patientPhone={patient.phoneNumber}
                patientPreferredFullName={patientPreferredFullName}
                selectedSurveyResults={assessmentState?.selectedSurveyResults}
                selectedTaskTab={selectedTaskTab}
                setSelectedTaskTab={setSelectedTaskTab}
                surveys={getSurveysResponse?.surveys}
                tabsConfiguration={filteredPatientTabsCollections}
                updateAssessmentState={updateAssessmentState}
              />
            </ChakraBox>
          </ChakraFlex>
        </ChakraTabs>
      )}
    </ChakraBox>
  );
};

export default PatientProfile;
